// Revisado: 2026-02-21 [I18n Scan] - [Meta SEO y UI]
import React from 'react';
import { useTranslation } from 'react-i18next';
import FadeIn from '../../../shared/FadeIn';
import SEO from '../../../shared/SEO';
import useGuessWhoGame from './hooks/useGuessWhoGame';
import GuessConfirmationModal from './GuessConfirmationModal'; // NEW
import GuessWhoRules from './GuessWhoRules'; // NEW

const GuessWhoBoard = ({ socket, roomId, onLeave }) => {
    const { t } = useTranslation();

    const {
        loading,
        error,
        board,
        gameStatus,
        secretChar,
        eliminatedIds,
        toast,
        playersOnline,
        players,
        actionMode,
        cardsData,
        aptitudesData,
        opponentStats, // NEW
        gameState,  // NEW: Turn system state
        winner,     // NEW: Expose winner
        // NEW: Magic Question
        magicAttempts,
        handleCheckWin,

        setActionMode,
        handleCardClick,
        handleEndTurn,

        // NEW: Modal Props
        guessCandidate,
        confirmGuess,
        cancelGuess
    } = useGuessWhoGame(socket, roomId);

    // --- Feature: Character Hover Zoom ---
    const [hoveredCardId, setHoveredCardId] = React.useState(null);
    const [tooltipCoords, setTooltipCoords] = React.useState({ x: 0, y: 0 });
    const hoverTimerRef = React.useRef(null);

    const handleMouseEnter = (cardId, event) => {
        handleMouseMove(cardId, event);
    };

    const handleMouseMove = (cardId, event) => {
        // Clear any existing timer
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

        // Hide tooltip immediately while moving (Idle Detection)
        setHoveredCardId(null);

        // Capture coordinates
        const { clientX, clientY } = event;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Determine Quadrant (Smart Positioning)
        const isRightHalf = clientX > windowWidth / 2;
        const isBottomHalf = clientY > windowHeight / 2;

        const coords = {
            x: clientX,
            y: clientY,
            isRight: isRightHalf,
            isBottom: isBottomHalf
        };

        // Start 500ms timer
        hoverTimerRef.current = setTimeout(() => {
            setTooltipCoords(coords);
            setHoveredCardId(cardId);
        }, 500);
    };

    const handleMouseLeave = () => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        setHoveredCardId(null);
    };

    // Find data for currently hovered card
    // eslint-disable-next-line eqeqeq
    const tooltipData = hoveredCardId ? board.find(c => c.id == hoveredCardId) : null;
    // -------------------------------------

    // DEBUG: Log playersOnline in UI component
    React.useEffect(() => {
        console.log(`[GuessWhoBoard][${roomId}] 🎨 UI Component - playersOnline prop: ${playersOnline}`);
        console.log(`[GuessWhoBoard][${roomId}] 🎨 UI Component - board.length: ${board.length}`);
        console.log(`[GuessWhoBoard][${roomId}] 🎨 UI Component - Should show board? ${playersOnline >= 2 && board.length > 0}`);
    }, [playersOnline, board.length, roomId]);

    if (loading) return <div className="text-white text-center p-10">{t('GuessWho.loading', 'Loading Data...')}</div>;
    if (error) return <div className="text-red-400 text-center p-10">{error}</div>;

    return (
        <FadeIn>
            <SEO title={t('MetaGuessWhoBoard.title')} description={t('MetaGuessWhoBoard.description')} />
            <div className="max-w-7xl mx-auto pb-12 relative">
                {toast && (
                    <div className={`fixed top-24 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-2xl font-bold flex items-center gap-3 animate-bounce-in
                        ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-teal-500 text-white'}
                    `}>
                        <span className="text-2xl">{toast.type === 'error' ? '⚠️' : '✅'}</span>
                        {toast.msg}
                    </div>
                )}

                {/* NEW: Guess Confirmation Modal */}
                <GuessConfirmationModal
                    isOpen={!!guessCandidate}
                    candidate={guessCandidate}
                    onConfirm={confirmGuess}
                    onCancel={cancelGuess}
                />

                {/* GAME RULES SECTION - Collapsible, Open by Default */}
                <GuessWhoRules />

                {/* GAME HEADER & CONTROLS */}
                <div className="flex justify-between items-center mb-6 bg-gray-800 p-4 rounded-xl border border-gray-700">
                    <div className="flex items-center gap-4">
                        <button onClick={onLeave} className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                            {t('GuessWhoBoard.leave_room', { roomId })}
                        </button>
                        <div className="flex flex-col gap-1">
                            {players.length > 0 ? (
                                players.map((p, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs font-mono bg-gray-900/80 px-3 py-1 rounded border border-gray-700">
                                        <span className={`w-2 h-2 rounded-full ${p.status === 'Online' ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></span>
                                        <span className={p.isMe ? 'text-teal-400 font-bold' : 'text-gray-300'}>
                                            {p.name}
                                        </span>
                                        <span className="text-gray-500 text-[10px]">({p.status})</span>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-500 text-xs italic">{t('GuessWhoBoard.connecting')}</div>
                            )}
                        </div>
                    </div>


                </div>

                {/* NEW: TURN & READINESS INDICATORS */}
                {playersOnline >= 2 && gameStatus !== 'waiting' && (
                    <div className="mb-4 flex gap-4">
                        {/* Turn Indicator */}
                        {gameState.status === 'playing' && (
                            <div className={`flex-1 p-4 rounded-xl border-2 ${gameState.isMyTurn
                                ? 'bg-green-900/30 border-green-500'
                                : 'bg-gray-800 border-gray-600'
                                }`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${gameState.isMyTurn ? 'bg-green-500 animate-pulse' : 'bg-gray-500'
                                            }`}></div>
                                        <span className="text-white font-bold">
                                            {gameState.isMyTurn ? t('GuessWhoBoard.your_turn') : t('GuessWhoBoard.opponents_turn')}
                                        </span>
                                    </div>
                                    <span className="text-gray-400 text-sm">
                                        {t('GuessWhoBoard.turn_counter', { turnNumber: gameState.turnNumber })}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Readiness Status */}
                        {gameState.status === 'picking' && (
                            <div className="flex-1 p-4 rounded-xl bg-gray-800 border border-gray-700">
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="text-white">{t('GuessWhoBoard.you_ready')}</span>
                                        <span className={gameState.iAmReady ? 'text-green-500' : 'text-yellow-500'}>
                                            {gameState.iAmReady ? t('GuessWhoBoard.status_ready') : t('GuessWhoBoard.status_picking')}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-white">{t('GuessWhoBoard.opponent_ready')}</span>
                                        <span className={gameState.opponentReady ? 'text-green-500' : 'text-yellow-500'}>
                                            {gameState.opponentReady ? t('GuessWhoBoard.status_ready') : t('GuessWhoBoard.status_picking')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Opponent Stats Display (NEW) */}
                        {gameState.status === 'playing' && (
                            <div className="flex-1 p-4 rounded-xl bg-gray-800 border-2 border-red-900/40">
                                <div className="flex items-center justify-between h-full">
                                    <div className="text-xs text-red-400 font-bold uppercase tracking-widest mb-1">{t('GuessWhoBoard.opponent_board')}</div>
                                    <div className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <span className="text-2xl font-black text-white leading-none">{opponentStats.unflipped}</span>
                                            <span className="text-[10px] text-gray-500 uppercase">{t('GuessWhoBoard.remaining')}</span>
                                        </div>
                                        <div className="w-px bg-gray-700 h-8 self-center"></div>
                                        <div className="flex flex-col items-center">
                                            <span className="text-2xl font-black text-gray-400 leading-none">{opponentStats.flipped}</span>
                                            <span className="text-[10px] text-gray-600 uppercase">{t('GuessWhoBoard.eliminated')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TOOLTIP (New Feature: Character Hover Zoom) */}
                {hoveredCardId && tooltipData && (
                    <div
                        className="fixed z-50 pointer-events-none transition-opacity duration-200 animate-fade-in"
                        style={{
                            // ANCHOR LOGIC:
                            // If Right Half -> Anchor RIGHT edge to cursor LEFT (windowWidth - x + 20px)
                            // If Left Half  -> Anchor LEFT edge to cursor RIGHT (x + 20px)
                            left: tooltipCoords.isRight ? 'auto' : `${tooltipCoords.x + 20}px`,
                            right: tooltipCoords.isRight ? `${window.innerWidth - tooltipCoords.x + 20}px` : 'auto',

                            // If Bottom Half -> Anchor BOTTOM edge to cursor TOP (windowHeight - y + 20px)
                            // If Top Half    -> Anchor TOP edge to cursor BOTTOM (y + 20px)
                            top: tooltipCoords.isBottom ? 'auto' : `${tooltipCoords.y + 20}px`,
                            bottom: tooltipCoords.isBottom ? `${window.innerHeight - tooltipCoords.y + 20}px` : 'auto'
                        }}
                    >
                        <div className="bg-gray-900 border-4 border-teal-500 rounded-2xl shadow-2xl p-2 w-80">
                            <img
                                src={tooltipData.img}
                                alt={tooltipData.name}
                                className="w-full h-auto rounded-xl object-cover mb-2"
                            />
                            <div className="text-center">
                                <p className="text-white font-bold text-lg leading-tight">{tooltipData.name}</p>
                                <p className="text-teal-400 text-xs font-mono mt-1">{t('GuessWhoBoard.full_resolution')}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col lg:flex-row gap-4 items-start">
                    {/* LEFT COLUMN: STATUS & YOU ARE */}
                    <div className="w-full lg:w-64 lg:sticky lg:top-4 z-40 shrink-0">
                        <div className={`text-center mb-6 transition-all duration-300 ${gameStatus === 'playing' ? 'bg-gray-800/50 p-6 rounded-2xl border border-gray-700 backdrop-blur-md' : 'py-4'}`}>
                            {gameStatus === 'waiting' && <p className="text-xl text-gray-400 font-light">{t('GuessWhoBoard.waiting_players')}</p>}
                            {gameStatus === 'picking' && <p className="text-xl text-teal-400 font-bold animate-pulse">{t('GuessWhoBoard.pick_character')}</p>}
                            {gameStatus === 'playing' && secretChar && (
                                <div className="flex flex-col items-center gap-4">
                                    <div>
                                        <p className="text-gray-400 text-xs font-bold tracking-widest uppercase mb-1">{t('GuessWhoBoard.you_are')}</p>
                                        <div className="relative inline-block">
                                            <img src={secretChar.img} alt="Secret" className="w-48 h-48 rounded-full border-4 border-teal-500 shadow-lg object-cover bg-gray-900" />
                                            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white text-xs px-3 py-1 rounded-full border border-gray-900 font-bold shadow-lg">{t('GuessWhoBoard.secret')}</div>
                                        </div>
                                    </div>
                                    <p className="text-xl font-black text-white tracking-wide border-b border-gray-700 pb-2 w-full truncate">{secretChar.name}</p>

                                    {/* Aptitudes & Unique Skill Info */}
                                    <div className="w-full text-left space-y-3 bg-gray-900/40 p-3 rounded-lg border border-gray-700/50">
                                        {/* Unique Skill */}
                                        {(() => {
                                            // 1. Get Aptitudes (Logic Preserved)
                                            // eslint-disable-next-line eqeqeq
                                            const originalCard = cardsData.find(c => c.Id_Personaje == secretChar.id && c.reference === 'Original');

                                            // Aptitudes Retrieval
                                            let charAptitudes = null;
                                            if (originalCard && aptitudesData.length > 0) {
                                                // Try finding by card_id
                                                // eslint-disable-next-line eqeqeq
                                                charAptitudes = aptitudesData.find(a => a.card_id == originalCard.card_id);
                                                // Fallback to character ID
                                                if (!charAptitudes) {
                                                    // eslint-disable-next-line eqeqeq
                                                    charAptitudes = aptitudesData.find(a => a.Id_Personaje == secretChar.id);
                                                }
                                            }

                                            // Filter for Rank A
                                            const aptKeys = ['turf', 'dirt', 'short_distance', 'mile', 'medium_distance', 'long_distance', 'runner', 'leader', 'betweener', 'chaser'];
                                            const goodAptitudes = charAptitudes
                                                ? aptKeys.filter(key => charAptitudes[key] === 'A')
                                                : [];

                                            // 2. Get Known Suits (New Feature)
                                            // eslint-disable-next-line eqeqeq
                                            const knownSuits = cardsData.filter(c => c.Id_Personaje == secretChar.id);

                                            return (
                                                <>
                                                    {/* Suit Names List */}
                                                    {knownSuits.length > 0 && (
                                                        <div className="mb-2">
                                                            <p className="text-[10px] text-gray-500 uppercase font-bold mb-0.5">{t('GuessWhoBoard.known_suits')}</p>
                                                            <ul className="text-xs text-teal-300 font-bold space-y-0.5 pl-1">
                                                                {knownSuits.map(suit => (
                                                                    <li key={suit.card_id} className="truncate" title={suit.title_en}>
                                                                        • {suit.title_en}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/* Aptitudes (Preserved) */}
                                                    {goodAptitudes.length > 0 && (
                                                        <div>
                                                            <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">{t('GuessWhoBoard.aptitude_a')}</p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {goodAptitudes.map(apt => (
                                                                    <span key={apt} className="px-1.5 py-0.5 bg-gray-700 rounded text-[10px] text-gray-300 capitalize">
                                                                        {t(`AptitudeValues.${apt}`, apt)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* RELOCATED CONTROLS */}
                            {gameStatus === 'playing' && (
                                <div className="mt-6 w-full space-y-3">
                                    {/* MAGIC QUESTION BUTTON - NEW */}
                                    <button
                                        type="button"
                                        onClick={handleCheckWin}
                                        disabled={!gameState.isMyTurn || magicAttempts <= 0}
                                        className={`w-full py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 border
                                            ${magicAttempts > 0 && gameState.isMyTurn
                                                ? 'bg-indigo-900/50 text-indigo-300 border-indigo-500 hover:bg-indigo-800 hover:text-white hover:shadow-lg hover:shadow-indigo-500/20'
                                                : 'bg-gray-800 text-gray-600 border-gray-700 cursor-not-allowed'
                                            }
                                        `}
                                        title={magicAttempts <= 0 ? t('GuessWho.magic_btn_limit', 'No attempts remaining') : t('GuessWho.magic_btn_tooltip', 'Check if you can still win')}
                                    >
                                        <span className="text-base">🔮</span>
                                        <span>
                                            {t('GuessWho.magic_btn', `Magic Check (${magicAttempts})`, { count: magicAttempts })}
                                        </span>
                                    </button>

                                    <div className="bg-gray-900 rounded-lg p-1 flex w-full">
                                        <button
                                            type="button"
                                            onClick={() => setActionMode('flip')}
                                            className={`flex-1 py-2 rounded-md text-xs font-bold transition-colors uppercase tracking-wide
                                                ${actionMode === 'flip' ? 'bg-teal-600 text-white shadow-md' : 'text-gray-400 hover:text-white hover:bg-gray-800'}
                                            `}
                                        >
                                            {t('GuessWhoBoard.btn_flip')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setActionMode('guess')}
                                            disabled={!gameState.isMyTurn}
                                            className={`flex-1 py-2 rounded-md text-xs font-bold transition-colors uppercase tracking-wide
                                                ${!gameState.isMyTurn ? 'bg-gray-800 text-gray-600 cursor-not-allowed border border-gray-700' : ''}
                                                ${gameState.isMyTurn && actionMode === 'guess' ? 'bg-yellow-600 text-white shadow-md' : ''}
                                                ${gameState.isMyTurn && actionMode !== 'guess' ? 'text-gray-400 hover:text-white hover:bg-gray-800' : ''}
                                            `}
                                        >
                                            {t('GuessWhoBoard.btn_guess')}
                                        </button>
                                    </div>

                                    <button
                                        onClick={handleEndTurn}
                                        disabled={!gameState.isMyTurn}
                                        className={`w-full py-3 rounded-xl font-black text-sm uppercase tracking-wider shadow-lg transition-all transform active:scale-95
                                            ${gameState.isMyTurn
                                                ? 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white hover:scale-105 ring-2 ring-blue-500/20'
                                                : 'bg-gray-800 text-gray-600 cursor-not-allowed border border-gray-700'
                                            }`}
                                    >
                                        {t('GuessWhoBoard.btn_end_turn')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN: BOARD */}
                    <div className="flex-1 w-full relative">
                        {/* WAITING STATE - Hide Board if no opponent or board not generated */}
                        {playersOnline < 2 ? (
                            <div className="bg-gray-800/50 backdrop-blur-md rounded-3xl p-12 border border-gray-700 shadow-xl text-center flex flex-col items-center justify-center min-h-[400px]">
                                <div className="text-6xl animate-bounce mb-6">⏳</div>
                                <h2 className="text-2xl font-bold text-white mb-2">{t('GuessWhoBoard.waiting_opponent')}</h2>
                                <p className="text-gray-400 mb-6">{t('GuessWhoBoard.share_room')}</p>
                                <span className="font-mono bg-gray-900 border border-gray-600 px-4 py-2 rounded-lg text-teal-400 text-xl font-bold select-all tracking-wider shadow-inner">
                                    {roomId}
                                </span>
                            </div>
                        ) : (!board || board.length === 0) ? (
                            <div className="bg-gray-800/50 backdrop-blur-md rounded-3xl p-12 border border-gray-700 shadow-xl text-center flex flex-col items-center justify-center min-h-[400px]">
                                <div className="text-6xl animate-spin mb-6">⚙️</div>
                                <p className="text-gray-400 text-xl animate-pulse font-bold">{t('GuessWhoBoard.generating_board')}</p>
                            </div>
                        ) : (
                            <div className={`max-w-4xl mx-auto bg-gray-800/80 p-8 rounded-3xl border-4 shadow-2xl backdrop-blur-sm ${actionMode === 'guess' && gameStatus === 'playing' ? 'border-yellow-500/50' : 'border-gray-700'}`}>
                                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                                    {board.map(char => {
                                        const isEliminated = eliminatedIds.includes(char.id);
                                        return (
                                            <div
                                                key={char.id}
                                                onClick={() => handleCardClick(char.id)}
                                                onMouseEnter={(e) => handleMouseEnter(char.id, e)}
                                                onMouseMove={(e) => handleMouseMove(char.id, e)}
                                                onMouseLeave={handleMouseLeave}
                                                className={`
                                                    relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 border shadow-md
                                                    ${gameStatus === 'picking' ? 'hover:scale-105 hover:border-teal-400 border-gray-600' : ''}
                                                    ${gameStatus === 'playing' && !isEliminated && actionMode === 'flip' ? 'border-gray-500 hover:border-white' : ''}
                                                    ${gameStatus === 'playing' && !isEliminated && actionMode === 'guess' ? 'border-yellow-600 hover:border-yellow-400 hover:scale-105 ring-2 ring-yellow-500/20' : ''}
                                                    ${isEliminated ? 'border-gray-800 opacity-30 grayscale scale-95' : 'bg-gray-800'}
                                                `}
                                            >
                                                <div className="aspect-[3/4] relative">
                                                    <img src={char.img} alt={char.name} className="w-full h-full object-cover" loading="lazy" />
                                                    <div className="absolute bottom-0 w-full bg-black/80 backdrop-blur-sm p-1 text-center">
                                                        <p className="text-white text-[10px] font-bold truncate leading-tight">{char.name}</p>
                                                    </div>
                                                    {isEliminated && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                            <span className="text-red-500 text-6xl font-black leading-none select-none">×</span>
                                                        </div>
                                                    )}
                                                    {gameStatus === 'picking' && (
                                                        <div className="absolute inset-0 bg-teal-500/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <span className="bg-teal-600 text-white px-2 py-1 rounded text-xs font-bold shadow-sm">{t('GuessWhoBoard.pick_me')}</span>
                                                        </div>
                                                    )}
                                                    {gameStatus === 'playing' && actionMode === 'guess' && !isEliminated && (
                                                        <div className="absolute inset-0 bg-yellow-500/10 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <span className="bg-yellow-600 text-white px-2 py-1 rounded text-xs font-bold shadow-sm">{t('GuessWhoBoard.guess_q')}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* GAME OVER OVERLAY */}
                {gameStatus === 'game_over' && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md rounded-3xl animate-fade-in">
                        <div className="text-center p-12 bg-gray-900 border-4 border-teal-500 rounded-3xl shadow-2xl relative overflow-hidden transform transition-all scale-100 max-w-lg w-full mx-4">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-500 via-purple-500 to-teal-500 animate-gradient-x"></div>

                            <h2 className={`text-6xl font-black mb-4 tracking-tighter drop-shadow-lg ${winner === parseInt(localStorage.getItem('userId')) ? 'text-teal-400' : 'text-red-500'}`}>
                                {winner === parseInt(localStorage.getItem('userId')) ? t('GuessWhoBoard.victory') : t('GuessWhoBoard.defeat')}
                            </h2>

                            <div className="text-9xl mb-8 animate-bounce filter drop-shadow-md">
                                {winner === parseInt(localStorage.getItem('userId')) ? '🏆' : '💀'}
                            </div>

                            <p className="text-xl text-gray-300 mb-8 font-bold">
                                {winner === parseInt(localStorage.getItem('userId'))
                                    ? t('GuessWhoBoard.outsmarted')
                                    : t('GuessWhoBoard.guessed_correctly')}
                            </p>

                            <button
                                onClick={onLeave}
                                className="bg-teal-600 hover:bg-teal-500 text-white text-xl font-bold py-4 px-10 rounded-full shadow-lg transform transition hover:scale-105 active:scale-95 ring-4 ring-teal-500/30"
                            >
                                {t('GuessWhoBoard.back_lobby')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </FadeIn>
    );
};

export default GuessWhoBoard;
